/*!
 * Copyright © 2026. Verizon Connect Ireland Limited. All rights reserved.
 */

import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  buildOverrideVisualizations,
  EManagePostedSpeedTabs,
  IOverrideVisualization,
  ISegmentGroup,
  ISegmentGroupCard,
  RoadDirection
} from '../../models';
import { IManagePostedSpeedState } from '../manage-posted-speed.state';

export const selectManagePostedSpeedState = createFeatureSelector<IManagePostedSpeedState>('managePostedSpeed');

export const selectActiveTab = createSelector(selectManagePostedSpeedState, (state: IManagePostedSpeedState) => state.activeTab);

/**
 * Returns true when at least one segment is selected, enabling the
 * "Update Posted Speed" button.
 */
export const selectIsUpdateButtonEnabled = createSelector(
  selectManagePostedSpeedState,
  (state: IManagePostedSpeedState) => Object.keys(state.selectedSegments).length > 0
);

export const selectMapCenter = createSelector(selectManagePostedSpeedState, (state: IManagePostedSpeedState) => state.mapCenter);

export const selectCanUndo = createSelector(selectManagePostedSpeedState, (state: IManagePostedSpeedState) => state.canUndo);

export const selectCanRedo = createSelector(selectManagePostedSpeedState, (state: IManagePostedSpeedState) => state.canRedo);

export const selectUpdateFormSelectedRoads = createSelector(
  selectManagePostedSpeedState,
  (state: IManagePostedSpeedState) => state.updateFormSelectedRoads
);

export const selectPostedSpeedUpdates = createSelector(
  selectManagePostedSpeedState,
  (state: IManagePostedSpeedState) => state.postedSpeedUpdates
);

/** Number of items returned by the current query (filtered/bounded count). */
export const selectTotalItems = createSelector(selectManagePostedSpeedState, (state: IManagePostedSpeedState) => state.totalItems);

/** Total account-wide override count regardless of filters or bounds. */
export const selectTotalAccountOverrides = createSelector(
  selectManagePostedSpeedState,
  (state: IManagePostedSpeedState) => state.totalAccountOverrides
);

export const selectFilters = createSelector(selectManagePostedSpeedState, (state: IManagePostedSpeedState) => state.filters);

/**
 * Returns the loaded overrides directly. Filtering is now performed server-side
 * via the API query parameters (statuses, startDate, endDate).
 */
export const selectFilteredPostedSpeedUpdates = selectPostedSpeedUpdates;

export const selectAllowMapRefine = createSelector(selectManagePostedSpeedState, (state: IManagePostedSpeedState) => state.allowMapRefine);

export const selectIsUpdateFormOpen = createSelector(
  selectManagePostedSpeedState,
  (state: IManagePostedSpeedState) => state.isUpdateFormOpen
);

export const selectUpdateSpeedLimitForm = createSelector(
  selectManagePostedSpeedState,
  (state: IManagePostedSpeedState) => state.updateSpeedLimitForm
);

export const selectDrawerState = createSelector(selectManagePostedSpeedState, (state: IManagePostedSpeedState) => state.drawerState);

export const selectIsSegmentSelected = createSelector(
  selectManagePostedSpeedState,
  (state: IManagePostedSpeedState) => state.isSegmentSelected
);

export const selectEditingOverrideId = createSelector(
  selectManagePostedSpeedState,
  (state: IManagePostedSpeedState) => state.editingOverrideId
);

export const selectSelectedSegmentRef = createSelector(
  selectManagePostedSpeedState,
  (state: IManagePostedSpeedState) => state.selectedSegmentRef
);

export const selectSegmentInfo = createSelector(selectManagePostedSpeedState, (state: IManagePostedSpeedState) => state.segmentInfo);

export const selectHoveredUpdateId = createSelector(
  selectManagePostedSpeedState,
  (state: IManagePostedSpeedState) => state.hoveredUpdateId
);

export const selectSelectedUpdateId = createSelector(
  selectManagePostedSpeedState,
  (state: IManagePostedSpeedState) => state.selectedUpdateId
);

/**
 * Combines override data with the selected segment reference to produce
 * an array of {@link IOverrideVisualization} objects for map rendering.
 *
 * Filters out overrides without valid geometry and excludes the primary
 * selected segment. Maps the API-provided status to the corresponding color.
 * Stamps `isHovered` and `isSelected` flags from the store hover/selection state.
 */
export const selectOverrideVisualizations = createSelector(
  selectPostedSpeedUpdates,
  selectSelectedSegmentRef,
  selectHoveredUpdateId,
  selectSelectedUpdateId,
  selectEditingOverrideId,
  (overrides, selectedSegmentRef, hoveredUpdateId, selectedUpdateId, editingOverrideId): IOverrideVisualization[] => {
    return buildOverrideVisualizations(overrides, selectedSegmentRef, hoveredUpdateId, selectedUpdateId, editingOverrideId);
  }
);

/**
 * Projects the computed segment groups from state.
 * Each group represents a contiguous chain of selected segments on the same road.
 */
export const selectSegmentGroups = createSelector(selectManagePostedSpeedState, (state: IManagePostedSpeedState) => state.segmentGroups);

/**
 * When the edit form is open, extracts the editing override's geometry
 * and wraps it as an {@link ISegmentGroup} so the SegmentHighlightRenderer
 * draws it with the solid blue "creation" style instead of the dotted overlay.
 */
export const selectEditingOverrideSegmentGroup = createSelector(
  selectPostedSpeedUpdates,
  selectEditingOverrideId,
  (overrides, editingOverrideId): ISegmentGroup | null => {
    if (!editingOverrideId) {
      return null;
    }

    const override = overrides.find(o => o.id === editingOverrideId);
    if (!override?.geometry || override.geometry.length < 2) {
      return null;
    }

    return {
      groupId: `edit-${override.id}`,
      segments: [],
      mergedRoadDetails: {
        roadName: override.roadName,
        cityName: override.cityName,
        speedLimitInKmh: override.speedOverwriteInKmh,
        direction: (override.segmentDirection || override.direction).toLowerCase() as RoadDirection,
        distanceKm: override.distanceKm
      },
      mergedGeometry: override.geometry
    };
  }
);

/**
 * Returns segment groups when the Update tab is active, or the editing override
 * segment group when the Edit/Delete form is open for a specific override.
 * When neither condition is met, emits an empty array to clear the map highlight.
 */
export const selectVisibleSegmentGroups = createSelector(
  selectSegmentGroups,
  selectActiveTab,
  selectEditingOverrideSegmentGroup,
  (groups, activeTab, editingGroup) => {
    if (activeTab === EManagePostedSpeedTabs.Update) {
      return groups;
    }
    return editingGroup ? [editingGroup] : [];
  }
);

/**
 * Maps each segment group to its merged road details for card rendering.
 * Returns one {@link ISegmentGroupCard} per group, preserving group order.
 * Includes the groupId so the UI can dispatch removal actions.
 */
export const selectSegmentGroupCards = createSelector(selectSegmentGroups, (groups): ISegmentGroupCard[] =>
  groups.map(group => ({ ...group.mergedRoadDetails, groupId: group.groupId }))
);

export const selectSegmentError = createSelector(selectManagePostedSpeedState, (state: IManagePostedSpeedState) => state.segmentError);

export const selectOriginalFormValues = createSelector(
  selectManagePostedSpeedState,
  (state: IManagePostedSpeedState) => state.originalFormValues
);

/**
 * Returns true when the current form values differ from the original values captured
 * on entering the edit flow. Returns true unconditionally for the create flow (no original values).
 */
export const selectIsFormDirty = createSelector(selectUpdateSpeedLimitForm, selectOriginalFormValues, (current, original) => {
  if (!original) return true;
  return (
    current.updatedPostedSpeedLimit !== original.updatedPostedSpeedLimit ||
    current.directionOfEdit !== original.directionOfEdit ||
    current.startDate !== original.startDate ||
    (current.endDate || null) !== (original.endDate || null) ||
    (current.notes || '') !== (original.notes || '')
  );
});

export const selectLastSavedOverrideId = createSelector(
  selectManagePostedSpeedState,
  (state: IManagePostedSpeedState) => state.lastSavedOverrideId
);

/** Projects whether a posted speed override save is currently in progress. */
export const selectIsSaving = createSelector(selectManagePostedSpeedState, (state: IManagePostedSpeedState) => state.isSaving);
